import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Users, ArrowLeft } from "lucide-react";
import { Layout } from "@/frontend/components/layout/Layout";
import { Button } from "@/frontend/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/frontend/components/ui/breadcrumb";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/frontend/components/ui/carousel";
import { useRoomType } from "@/backend/hooks/useRoomType";
import { useResidence } from "@/backend/hooks/useResidence";
import { isDraft } from "@/backend/lib/residenceUtils";
import { ResidenceCardSkeleton } from "@/frontend/components/residences/ResidenceCardSkeleton";
import { PolicyLinks } from "@/frontend/components/residences/PolicyLinks";
import { CommonAreasSection } from "@/frontend/components/residences/CommonAreasSection";
import { ImageLightbox, type LightboxImage } from "@/frontend/components/residences/ImageLightbox";

const RoomDetail = () => {
  const { id: residenceId, roomId } = useParams<{ id: string; roomId: string }>();
  const navigate = useNavigate();
  const { data: roomType, isLoading: roomLoading, error: roomError } = useRoomType(roomId);
  const { data: residenceData, isLoading: residenceLoading, error: residenceError } = useResidence(residenceId);

  // Hide unpublished content from direct URLs: a draft room, or a room whose
  // residence is a draft (useResidence surfaces that as an error / no data).
  const notFound =
    roomError || !roomType || isDraft(roomType) || residenceError || !residenceData;

  // Fullscreen viewer state: which set of images + starting index, or null.
  const [lightbox, setLightbox] = useState<{ images: LightboxImage[]; index: number } | null>(null);

  const isLoading = roomLoading || residenceLoading;

  if (isLoading) {
    return (
      <Layout>
        <div className="pt-24 md:pt-28 min-h-screen">
          <div className="container-narrow section-padding">
            <ResidenceCardSkeleton />
          </div>
        </div>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout>
        <div className="pt-24 md:pt-28 min-h-screen">
          <div className="container-narrow section-padding text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground">
              Room not found
            </h1>
            <p className="mt-4 text-muted-foreground">
              The room you're looking for doesn't exist or has been removed.
            </p>
            <Button asChild className="mt-6">
              <Link to={residenceId ? `/residences/${residenceId}` : "/residences"}>
                Back to Residence
              </Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const residence = residenceData?.residence;

  return (
    <Layout>
      <div className="pt-24 md:pt-28">
        <div className="container-narrow section-padding">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-8">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/residences">Residences</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              {residence && (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to={`/residences/${residence.id}`}>{residence.name}</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                </>
              )}
              <BreadcrumbItem>
                <BreadcrumbPage>{roomType.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Back Button */}
          {residence && (
            <Button
              variant="ghost"
              className="mb-6"
              onClick={() => navigate(`/residences/${residence.id}`)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to {residence.name}
            </Button>
          )}

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Left Column - Room Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Room Header */}
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
                  {roomType.name}
                </h1>
                {residence && (
                  <p className="mt-2 text-muted-foreground">
                    At {residence.name}
                  </p>
                )}
              </div>

              {/* Room Images Slider */}
              {roomType.imagesUrl.length > 0 && (
                <div>
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                    Room Photos
                  </h2>
                  <div className="relative">
                    <Carousel className="w-full" opts={{ loop: roomType.imagesUrl.length > 1 }}>
                      <CarouselContent>
                        {roomType.imagesUrl.map((image, index) => (
                          <CarouselItem key={index}>
                            <button
                              type="button"
                              onClick={() =>
                                setLightbox({
                                  images: roomType.imagesUrl.map((url) => ({ url })),
                                  index,
                                })
                              }
                              className="relative block w-full aspect-[4/3] bg-muted rounded-xl overflow-hidden cursor-zoom-in"
                            >
                              <img
                                src={image}
                                alt={`${roomType.name} - Photo ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              {/* Slide counter */}
                              {roomType.imagesUrl.length > 1 && (
                                <div className="absolute top-2 right-2 rounded-full bg-black/60 px-2.5 py-0.5 text-xs font-medium text-white">
                                  {index + 1} / {roomType.imagesUrl.length}
                                </div>
                              )}
                            </button>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      {roomType.imagesUrl.length > 1 && (
                        <>
                          <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2" />
                          <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2" />
                        </>
                      )}
                    </Carousel>
                  </div>
                </div>
              )}

              {/* Residence Common Areas */}
              <CommonAreasSection commonAreas={residence?.commonAreas} />

              {/* Floor Plan Image */}
              {roomType.floorPlanUrl && (
                <div>
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                    Floor Plan
                  </h2>
                  <button
                    type="button"
                    onClick={() =>
                      setLightbox({
                        images: [{ url: roomType.floorPlanUrl, caption: "Floor plan" }],
                        index: 0,
                      })
                    }
                    className="relative block w-full aspect-[4/3] bg-muted rounded-xl overflow-hidden cursor-zoom-in"
                  >
                    <img
                      src={roomType.floorPlanUrl}
                      alt={`Floor plan of ${roomType.name}`}
                      className="w-full h-full object-contain"
                    />
                  </button>
                </div>
              )}

              {/* Room Description */}
              <div>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  Description
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {roomType.description}
                </p>
              </div>

              {/* Room Specifications */}
              <div>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  Room Specifications
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-card border border-border rounded-lg">
                    <p className="text-sm text-muted-foreground">Room Size</p>
                    <p className="text-lg font-semibold text-foreground mt-1">
                      {roomType.area} m²
                    </p>
                  </div>
                  <div className="p-4 bg-card border border-border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Max Occupancy</p>
                        <p className="text-lg font-semibold text-foreground mt-1">
                          {roomType.maxOccupancy} {roomType.maxOccupancy === 1 ? 'person' : 'people'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Booking Card */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 bg-card border border-border rounded-xl p-6 shadow-sm">
                <div className="mb-6">
                  <span className="text-sm text-muted-foreground">Monthly Price</span>
                  {(() => {
                    const prices = roomType.priceByOccupancy;
                    if (prices.length === 1) {
                      return (
                        <p className="text-3xl font-heading font-bold text-foreground">
                          €{prices[0]}
                          <span className="text-base font-normal text-muted-foreground">/month</span>
                        </p>
                      );
                    }
                    return (
                      <div className="mt-2 space-y-1">
                        {prices.map((price, i) => (
                          <div key={i} className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">
                              {i + 1} {i === 0 ? 'student' : 'students'}
                            </span>
                            <span className="font-semibold text-foreground">
                              €{price}<span className="font-normal text-muted-foreground">/mo</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  Minimum stay: {roomType.minStay || 1} month{(roomType.minStay || 1) > 1 ? 's' : ''}
                </p>
                <Button
                  variant="coral"
                  size="lg"
                  className="w-full"
                  onClick={() => navigate(`/book?residence=${residenceId}&roomType=${roomId}`)}
                >
                  Book Now
                </Button>
                {residenceId && <PolicyLinks residenceId={residenceId} />}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ImageLightbox
        images={lightbox?.images ?? []}
        open={lightbox !== null}
        startIndex={lightbox?.index ?? 0}
        onOpenChange={(open) => {
          if (!open) setLightbox(null);
        }}
        title={roomType.name}
      />
    </Layout>
  );
};

export default RoomDetail;

